import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  createColumnHelper,
} from "@tanstack/react-table";
import { FormEvent, useContext, useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import Loading from "../Loading";
import FormError from "../components/FormError";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import {
  AssignedToFilter,
  multiSelectIncludes,
} from "../components/Table/filters";
import {
  useSlantConnected,
  makeServiceTierColumn,
} from "../components/Table/serviceTier";
import {
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  useTableSettings,
} from "../components/Table/tableSettings";
import { useAuthenticatedPercentFetch } from "../clients/percents";
import {
  processEmptyResponse,
  refusalSentence,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { parseISODateNaive, toISODateNoTimezone } from "../lib/date";
import { formatCurrency } from "../lib/numbers";
import {
  RebalanceReasonFilter,
  RebalanceRow,
  getRealizedGainLoss,
  getRiskAllocationDisplay,
  getSelectedRebalanceRows,
  rebalanceTypeDisplay,
  rebalanceTypeFilterOptions,
} from "./lib";
import RebalanceQueryControls from "./RebalanceQueryControls";
import { deserializeRebalanceListPercents } from "./percents";
import {
  getRebalanceRowId,
  getRebalanceSubRows,
  useRebalanceListRows,
  withoutServiceTierFilter,
} from "./useRebalanceListRows";
import { validateRqlQuery } from "../lib/rql";

const columnHelper = createColumnHelper<RebalanceRow>();

/**
 * The `executedFrom`/`executedTo` of `GET /v1/rebalances` for the days `from`
 * through `to` (`YYYY-MM-DD`) in the browser's timezone: from the start of
 * `from` to the start of the day after `to`. Today's, while either is not a
 * whole date with a four-digit year (a date input being cleared, or a year
 * being typed: the input reports `0002-10-19` on the way to `2026-10-19`) or
 * `from` is after `to`. `today` is true while it asks for today in place of
 * `from` and `to` (R8).
 */
export function executedTimeRangeQuery(from: string, to: string) {
  const wholeDate = /^[1-9]\d{3}-\d{2}-\d{2}$/;
  let start: Date;
  let end: Date;
  let today = false;
  try {
    if (!wholeDate.test(from) || !wholeDate.test(to)) {
      throw new Error(`Not a whole date: ${from} to ${to}`);
    }
    start = parseISODateNaive(from);
    end = parseISODateNaive(to);
  } catch {
    start = parseISODateNaive(toISODateNoTimezone(new Date()));
    end = new Date(start);
    today = true;
  }
  if (start > end) {
    start = parseISODateNaive(toISODateNoTimezone(new Date()));
    end = new Date(start);
    today = true;
  }
  end.setDate(end.getDate() + 1);
  return {
    query: `&executedFrom=${encodeURIComponent(start.toISOString())}&executedTo=${encodeURIComponent(end.toISOString())}`,
    today,
  };
}

const RebalancesExecutedTodayTable = ({
  householdId,
}: {
  householdId?: string;
}) => {
  const [page] = useState(1);
  const [pageSize] = useState(1000); // APP-1308: show all completed rebalances on one page (client-side pager handles display); server pager is the proper follow-up
  const [rqlQuery, setRqlQuery] = useState("");
  // The days the rebalances were executed on, in the browser's timezone. Held
  // in component state, not in the table settings, so the tab opens on today
  // every time it is visited.
  const [fromDate, setFromDate] = useState(() =>
    toISODateNoTimezone(new Date()),
  );
  const [toDate, setToDate] = useState(() => toISODateNoTimezone(new Date()));
  const { query: executedTimeQuery, today: datesNotValid } =
    executedTimeRangeQuery(fromDate, toDate);

  // APP-1279/APP-1280: filtering is pushed down to SQL — send the RQL as &q=.
  const rqlValidation = validateRqlQuery(rqlQuery);
  // APP-1652: `GET /rebalances/filter` is served at `GET /v1/rebalances` under
  // `limit`/`offset`. One page of `pageSize` rows, as before (APP-1308).
  const queryKey = `/v1/rebalances?status=complete&metrics=true&limit=${pageSize}&offset=${(page - 1) * pageSize}${executedTimeQuery}${
    householdId ? `&householdId=${householdId}` : ""
  }${
    rqlQuery.trim() && rqlValidation.success
      ? `&q=${encodeURIComponent(rqlQuery)}`
      : ""
  }`;

  const {
    isPending: isPendingRebalances,
    isError: isErrorRebalances,
    data: dataRebalances,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<RebalancesController["getAllExecutedRebalances"]>
  >(queryKey, deserializeRebalanceListPercents);
  const rebalances = dataRebalances?.data;
  // R27: the read is one page of `pageSize` rows; `totalCount` is how many the
  // days hold.
  const totalCount = dataRebalances?.pagination?.totalCount;

  // APP-1486: the API refuses to send a position swap's proposal back to
  // Suggested (409), and that refusal fails the whole bulk call. Its rows (and
  // its account rows) can't be selected.
  const swapRebalanceIds = useMemo(
    () =>
      new Set(
        (rebalances ?? [])
          .filter((rebalance) => rebalance.positionSwapId != null)
          .map((rebalance) => rebalance.id),
      ),
    [rebalances],
  );

  const slantConnected = useSlantConnected();

  const [tableSettings, setTableSettings] = useTableSettings(
    "rebalances-executed-today",
  );
  const {
    rebalanceRows,
    isPendingAssignees,
    filterBarItems,
    setRowSelectionRef,
  } = useRebalanceListRows(rebalances, tableSettings, setTableSettings);

  const queryClient = useQueryClient();

  // APP-1486, swap spec R120: the API refuses to send a position swap's
  // proposal back to Suggested with 409 and a sentence naming the swap. A list
  // a later write made out of date can still send such a proposal, so the
  // refusal is shown.
  const { pushNotification } = useContext(NotificationContext);

  const proposeRebalances = useAuthenticatedMutationAsync<
    UnpackResponse<RebalancesController["changeRebalancesState"]>
  >(
    "/rebalances",
    async () => {
      const rebalanceIds = getSelectedRebalanceRows(rowSelection).filter(
        (id) => !swapRebalanceIds.has(id),
      );

      return {
        method: "PATCH",
        body: JSON.stringify({
          state: "proposed",
          rebalanceIds,
        }),
      };
    },
    processEmptyResponse,
  );

  const mutation = useMutation({
    mutationFn: async (event: FormEvent) => {
      event.preventDefault();

      // Get these before the async request for data consistency
      const selectedRebalanceIds = getSelectedRebalanceRows(rowSelection);
      const unselectedRebalances = (rebalances ?? [])?.filter(
        (rebalance) => !selectedRebalanceIds.includes(rebalance.id),
      );

      await proposeRebalances();

      // Manually reset the row selection; otherwise the previous row selection
      // remains in the table state even after modifying its data.
      setRowSelection({});
      // The sent rows leave the days' total too, so the count message (R27)
      // stays right until the list is read again.
      const sentCount = (rebalances ?? []).length - unselectedRebalances.length;
      queryClient.setQueryData<typeof dataRebalances>([queryKey], (previous) =>
        previous
          ? {
              ...previous,
              data: unselectedRebalances,
              ...(previous.pagination && {
                pagination: {
                  ...previous.pagination,
                  totalCount: previous.pagination.totalCount - sentCount,
                },
              }),
            }
          : previous,
      );
    },
    onError: (error) =>
      pushNotification({
        id: "rebalances-executed-error",
        header: "Send to Suggested failed",
        body:
          refusalSentence(error) ??
          "Failed to send the trade proposals to Suggested.",
        variant: "danger",
      }),
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: (info) => (
          <Form.Check
            checked={info.table.getIsAllRowsSelected()}
            onChange={info.table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        aggregatedCell: (info) => (
          <Form.Check
            id={`select-${info.row.original.rebalanceId}`}
            checked={info.row.getIsSelected()}
            disabled={!info.row.getCanSelect()}
            onChange={info.row.getToggleSelectedHandler()}
          />
        ),
      }),
      columnHelper.accessor("name", {
        cell: (info) => (
          <div
            style={{
              // Since rows are flattened by default,
              // we can use the row.depth property
              // and paddingLeft to visually indicate the depth
              // of the row
              paddingLeft: `${info.row.depth * 2}rem`,
            }}
          >
            {info.getValue()}
          </div>
        ),
        aggregatedCell: (info) => (
          <>
            {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
            <Link to={`/rebalances/${info.row.original.rebalanceId}`}>
              {info.getValue()}
            </Link>
          </>
        ),
        header: () => "Household",
        minSize: 225,
      }),
      // Service Tier (Slant) — immediately after Household, a column and a
      // filter (R40); omitted entirely for a user not connected to Slant (F4).
      ...(slantConnected ? [makeServiceTierColumn(columnHelper)] : []),
      columnHelper.accessor(
        (row: RebalanceRow) => {
          if (row.level === "account" || typeof row.type === "undefined") {
            return "";
          } else if (
            row.type &&
            Object.prototype.hasOwnProperty.call(rebalanceTypeDisplay, row.type)
          ) {
            const option = rebalanceTypeFilterOptions.find(
              (a) =>
                a.label ===
                rebalanceTypeDisplay[
                  row.type as keyof typeof rebalanceTypeDisplay
                ],
            );
            return option?.value.toString() ?? "";
          }
          return "";
        },
        {
          id: "type",
          header: () => "Trade Proposal Reason",
          cell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          aggregatedCell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: RebalanceReasonFilter,
          },
        },
      ),
      columnHelper.accessor((row) => row.assignedUser?.id.toString() ?? "", {
        id: "assignedUserId",
        cell: (info) => info.row.original.assignedUser?.name ?? "",
        aggregatedCell: (info) => info.row.original.assignedUser?.name ?? "",
        header: () => "Assigned To",
        minSize: 100,
        filterFn: multiSelectIncludes,
        meta: {
          filterComponent: AssignedToFilter,
        },
      }),
      columnHelper.accessor(
        (row) =>
          row.level === "account" || typeof row.type === "undefined"
            ? ""
            : (row.actualExecutionDate?.toLocaleString() ?? ""),
        {
          id: "executionTime",
          header: () => "Execution Time",
          enableColumnFilter: false,
          enableSorting: true,
        },
      ),
      columnHelper.accessor((row) => row.riskAllocation?.current.growth ?? 0, {
        id: "current",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        header: () => "Current Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor(
        (row) => row.riskAllocation?.postRebalance.growth ?? 0,
        {
          id: "postRebalance",
          cell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          aggregatedCell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          header: () => "Post-Trade Proposal Allocation",
          enableColumnFilter: false,
          enableSorting: false,
          meta: { className: "text-nowrap" },
        },
      ),
      columnHelper.accessor((row) => row.riskAllocation?.target.growth ?? 0, {
        id: "target",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        header: () => "Target Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor((row) => row.totalBalance ?? 0, {
        id: "totalBalance",
        cell: (info) => formatCurrency(info.row.original.totalBalance),
        aggregatedCell: (info) =>
          formatCurrency(info.row.original.totalBalance),
        header: () => "AUM",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.cashBalance ?? 0, {
        id: "cashBalance",
        cell: (info) => formatCurrency(info.row.original.cashBalance),
        aggregatedCell: (info) => formatCurrency(info.row.original.cashBalance),
        header: () => "Cash",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.buyTotal ?? 0, {
        id: "buyTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Buy",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.sellTotal ?? 0, {
        id: "sellTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Sell",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor(
        (row) => (row.sellTotal ?? 0) - (row.buyTotal ?? 0),
        {
          id: "net",
          cell: (info) => formatCurrency(info.getValue()),
          aggregatedCell: (info) => formatCurrency(info.getValue()),
          header: () => "Net",
          enableColumnFilter: false,
          meta: {
            headerClassName: "text-end",
            className: "text-end",
          },
        },
      ),
      columnHelper.accessor((row) => getRealizedGainLoss(row) ?? 0, {
        id: "realizedGainLoss",
        cell: (info) => formatCurrency(getRealizedGainLoss(info.row.original)),
        aggregatedCell: (info) =>
          formatCurrency(getRealizedGainLoss(info.row.original)),
        header: () => "Realized Gain/Loss",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
    ],
    [queryClient, rebalances, slantConnected],
  );

  const [savedColumnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  // R45: a saved Service Tier filter applies while the user is connected to
  // Slant. `useSlantConnected` is false until GET /profile has loaded, so the
  // saved filter is kept and only left out of the table while the user is not
  // connected — the column it filters is then not in the table.
  const columnFilters = useMemo(
    () =>
      slantConnected
        ? savedColumnFilters
        : withoutServiceTierFilter(savedColumnFilters),
    [slantConnected, savedColumnFilters],
  );
  const [sorting, setSorting] = useState<SortingState>(tableSettings.sorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: rebalanceRows,
    enableRowSelection: (row) =>
      !swapRebalanceIds.has(row.original.rebalanceId),
    getRowId: getRebalanceRowId,
    getSubRows: getRebalanceSubRows,
    state: { columnFilters, sorting, pagination },
    onColumnFiltersChange: onColumnFiltersChange(
      columnFilters,
      setColumnFilters,
      tableSettings,
      setTableSettings,
    ),
    onSortingChange: onSortingChange(
      sorting,
      setSorting,
      tableSettings,
      setTableSettings,
    ),
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetPageIndex: true,
    initialState: {
      sorting: [{ id: "executionTime", desc: true }],
    },
    meta: {
      ...metaDefault,
    },
  });

  setRowSelectionRef.current = setRowSelection;

  // R42: a column filter change removes from the selection each row it hides,
  // a hidden rebalance and its account rows, so "Send to Suggested" sends only
  // rows shown.
  useEffect(() => {
    const shown = new Set(
      table.getFilteredRowModel().flatRows.map((row) => row.id),
    );
    setRowSelection((selection) =>
      Object.keys(selection).every((id) => shown.has(id))
        ? selection
        : Object.fromEntries(
            Object.entries(selection).filter(([id]) => shown.has(id)),
          ),
    );
  }, [columnFilters, table, setRowSelection]);

  // R47: the selection is of the old list's rows; a new query reads a new
  // list, so it is cleared.
  const handleRqlQueryChange = (query: string) => {
    setRowSelection({});
    setRqlQuery(query);
  };

  // The dates stay on the page while the rebalances of a new range load, and
  // when the read fails (R50), so a change of the dates reads again.
  return (
    <>
      <Row className="mb-3 align-items-end">
        <Col xs="auto">
          <Form.Group controlId="executed-from-date">
            <Form.Label>Executed from</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(event) => {
                // The selection is of the old range's rows; keep it and
                // "Send to Suggested" would act on rows no longer shown.
                setRowSelection({});
                setFromDate(event.target.value);
              }}
            />
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group controlId="executed-to-date">
            <Form.Label>to</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(event) => {
                // The selection is of the old range's rows; keep it and
                // "Send to Suggested" would act on rows no longer shown.
                setRowSelection({});
                setToDate(event.target.value);
              }}
            />
          </Form.Group>
        </Col>
        {datesNotValid && (
          <Col xs="auto">
            <Form.Text>
              The dates are not valid. The list shows the trade proposals
              executed today.
            </Form.Text>
          </Col>
        )}
      </Row>
      {isPendingRebalances || isPendingAssignees ? (
        <Loading />
      ) : (
        <>
          {/* R50: a failed read shows its error in place of the table, and no
              "Send to Suggested" for rows not shown. */}
          {!isErrorRebalances && (
            <Form onSubmit={mutation.mutate}>
              <Row className="mb-3">
                <Col>
                  <ActionButton
                    label="Send to Suggested"
                    icon="/icons/redo.svg"
                    variant="secondary"
                    type="submit"
                    disabled={mutation.isPending}
                  />
                </Col>
              </Row>
            </Form>
          )}
          {!isErrorRebalances &&
            rebalances &&
            totalCount != null &&
            totalCount > rebalances.length && (
              <p>
                Showing the first {rebalances.length} of {totalCount} executed
                trade proposals. Choose fewer days.
              </p>
            )}
          <RebalanceQueryControls
            query={rqlQuery}
            onQueryChange={handleRqlQueryChange}
            // R47: a board selection clears the selection, also for a board
            // whose query is the one shown.
            onBoardSelect={() => setRowSelection({})}
          />
          {isErrorRebalances ? (
            <FormError message="Failed to load the trade proposals." />
          ) : (
            <Table table={table} filterBarItems={filterBarItems} />
          )}
        </>
      )}
    </>
  );
};

export default RebalancesExecutedTodayTable;
